import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { firstValueFrom } from 'rxjs';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';
import {
  LINE_BROADCAST_AUDIENCES,
  LINE_BROADCAST_LIFF_PAGES,
  LINE_BROADCAST_MAX_ALT_TEXT,
  LINE_BROADCAST_MAX_BUBBLES,
  LINE_BROADCAST_MAX_BUBBLE_BODY,
  LINE_BROADCAST_MAX_BUBBLE_TITLE,
  LINE_BROADCAST_MAX_BUTTON_LABEL,
  LINE_BROADCAST_MAX_EXCLUDED,
  LINE_BROADCAST_MAX_IMAGE_BYTES,
  LINE_BROADCAST_MAX_IMAGE_TEXT,
  LINE_BROADCAST_MAX_NEWS,
  LINE_BROADCAST_MESSAGE_TYPES,
  LINE_BROADCAST_MESSAGE_TYPE_META,
  LINE_BROADCAST_STATUS_FILTERS,
  LINE_BROADCAST_STATUS_META,
  LineBroadcastActionResponse,
  LineBroadcastAudienceMember,
  LineBroadcastAudienceSummary,
  LineBroadcastAudienceType,
  LineBroadcastBubble,
  LineBroadcastDetail,
  LineBroadcastImage,
  LineBroadcastLinkType,
  LineBroadcastListItem,
  LineBroadcastMemberCards,
  LineBroadcastMessageType,
  LineBroadcastNewsOption,
  LineBroadcastProgress,
  LineBroadcastQuota,
  LineBroadcastStatus,
  createEmptyLineBroadcastBubble,
  createEmptyLineBroadcastDetail,
  createLineBroadcastImage,
  lineBroadcastMaxImages,
  mapLineBroadcastAudienceMember,
  mapLineBroadcastAudienceSummary,
  mapLineBroadcastDetail,
  mapLineBroadcastListItem,
  mapLineBroadcastMemberCards,
  mapLineBroadcastNewsOption,
  mapLineBroadcastProgress,
  mapLineBroadcastQuota,
  buildLiffLink,
  needsLiffUrl,
  normalizeLiffPath,
  toLineBroadcastSavePayload,
  toLineBroadcastTestPayload,
  validateLineBroadcastDraft,
} from '../../../../core/models/system-settings/line-broadcast.model';

@Component({
  selector: 'app-line-broadcasts',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './line-broadcasts.component.html',
  styleUrl: './line-broadcasts.component.css',
})
export class LineBroadcastsComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  readonly siteState = inject(SiteStateService);

  private static readonly PollIntervalMs = 1500;

  readonly audiences = LINE_BROADCAST_AUDIENCES;
  readonly statusFilters = LINE_BROADCAST_STATUS_FILTERS;
  readonly activityEntityTypes = AdminActivityEntityTypes;
  readonly maxBubbles = LINE_BROADCAST_MAX_BUBBLES;
  readonly maxAltText = LINE_BROADCAST_MAX_ALT_TEXT;
  readonly maxBubbleTitle = LINE_BROADCAST_MAX_BUBBLE_TITLE;
  readonly maxBubbleBody = LINE_BROADCAST_MAX_BUBBLE_BODY;
  readonly maxButtonLabel = LINE_BROADCAST_MAX_BUTTON_LABEL;

  items = signal<LineBroadcastListItem[]>([]);
  totalCount = signal(0);
  page = signal(1);
  readonly pageSize = 20;
  isLoading = signal(false);
  isSaving = signal(false);
  lastFetchedTime = signal<Date | null>(null);

  searchText = '';
  filterStatus = signal<LineBroadcastStatus | ''>('');

  viewMode = signal<'list' | 'edit'>('list');
  draft = signal<LineBroadcastDetail>(createEmptyLineBroadcastDetail());
  audienceSummary = signal<LineBroadcastAudienceSummary | null>(null);
  quota = signal<LineBroadcastQuota | null>(null);
  uploadingKey = signal<string | null>(null);
  testLineUid = '';
  showActivityHistory = false;

  isSendConfirmOpen = signal(false);
  isRetryConfirmOpen = signal(false);
  isDeleteConfirmOpen = signal(false);
  deleteTarget = signal<LineBroadcastListItem | null>(null);

  progress = signal<LineBroadcastProgress | null>(null);
  isProgressOpen = signal(false);

  readonly maxExcluded = LINE_BROADCAST_MAX_EXCLUDED;
  readonly membersPageSize = 20;
  showMemberList = signal(false);
  members = signal<LineBroadcastAudienceMember[]>([]);
  membersTotal = signal(0);
  membersPage = signal(1);
  membersSearch = '';
  isMembersLoading = signal(false);

  readonly messageTypes = LINE_BROADCAST_MESSAGE_TYPES;
  readonly maxImageText = LINE_BROADCAST_MAX_IMAGE_TEXT;
  readonly maxNews = LINE_BROADCAST_MAX_NEWS;
  readonly imageUploadKey = 'images';
  showNewsPicker = signal(false);
  newsOptions = signal<LineBroadcastNewsOption[]>([]);
  isNewsLoading = signal(false);
  newsSearch = '';
  readonly liffPages = LINE_BROADCAST_LIFF_PAGES;
  /** https://liff.line.me/{liffId} — internal links = {liffUrl}?redirect={path} */
  liffUrl = signal('');

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private summaryTimer: ReturnType<typeof setTimeout> | null = null;
  private membersRequestSeq = 0;
  private summaryRequestSeq = 0;
  private newsRequestSeq = 0;

  trackById = (_: number, item: LineBroadcastListItem) => item.id;
  trackByBubble = (_: number, bubble: LineBroadcastBubble) => bubble.uiKey;
  trackByImage = (_: number, image: LineBroadcastImage) => image.uiKey;
  trackByNews = (_: number, news: LineBroadcastNewsOption) => news.id;

  constructor() {
    effect(() => {
      this.siteState.selectedSite()?.id;
      untracked(() => this.onNavSiteChanged());
    });
  }

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.summaryTimer) clearTimeout(this.summaryTimer);
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize));
  }

  get isEditable(): boolean {
    const d = this.draft();
    return d.isEditable && (d.id > 0 ? this.canUpdate : this.canCreate);
  }

  /** recipients after site filter + unticked members (used for quota + confirm) */
  get selectedAudienceCount(): number | null {
    const summary = this.audienceSummary();
    if (!summary) return null;
    return this.draft().audienceType === 'monthly_active' ? summary.monthlyActiveSelected : summary.allMembersSelected;
  }

  get quotaInsufficient(): boolean {
    const q = this.quota();
    const count = this.selectedAudienceCount;
    return !!q && q.isSuccess && !q.isUnlimited && q.remaining !== null && count !== null && q.remaining < count;
  }

  get canShowActivityHistory(): boolean {
    return this.draft().id > 0;
  }

  statusMeta(status: LineBroadcastStatus) {
    return LINE_BROADCAST_STATUS_META[status] ?? LINE_BROADCAST_STATUS_META.draft;
  }

  audienceLabel(type: LineBroadcastAudienceType): string {
    return this.audiences.find((a) => a.value === type)?.label ?? type;
  }

  audienceCount(type: LineBroadcastAudienceType): number | null {
    const summary = this.audienceSummary();
    if (!summary) return null;
    return type === 'monthly_active' ? summary.monthlyActiveSelected : summary.allMembersSelected;
  }

  setStatusFilter(value: LineBroadcastStatus | ''): void {
    this.filterStatus.set(value);
    this.page.set(1);
    this.reload();
  }

  onSearch(): void {
    this.page.set(1);
    this.reload();
  }

  onPageChange(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page.set(next);
    this.reload();
  }

  openCreate(): void {
    if (!this.canCreate) return;
    this.stopPolling();
    this.draft.set(createEmptyLineBroadcastDetail());
    this.testLineUid = '';
    this.resetMemberList();
    this.resetNewsPicker();
    this.viewMode.set('edit');
    this.loadAudienceAndQuota();
  }

  openItem(item: LineBroadcastListItem): void {
    this.progress.set(null);
    this.resetMemberList();
    this.resetNewsPicker();
    this.loadDetail(item.id, true);
    this.testLineUid = '';
    this.loadAudienceAndQuota(false);
  }

  backToList(): void {
    this.stopPolling();
    this.isProgressOpen.set(false);
    this.progress.set(null);
    this.viewMode.set('list');
    this.draft.set(createEmptyLineBroadcastDetail());
    this.reload();
  }

  patchDraft(partial: Partial<LineBroadcastDetail>): void {
    this.draft.update((d) => ({ ...d, ...partial }));
  }

  /** mutate existing bubble so inputs keep focus while typing */
  patchBubble(uiKey: string, partial: Partial<LineBroadcastBubble>): void {
    this.draft.update((d) => {
      const bubble = d.bubbles.find((b) => b.uiKey === uiKey);
      if (bubble) Object.assign(bubble, partial);
      return { ...d, bubbles: d.bubbles };
    });
  }

  addBubble(): void {
    if (!this.isEditable || this.draft().bubbles.length >= this.maxBubbles) return;
    this.draft.update((d) => ({ ...d, bubbles: [...d.bubbles, createEmptyLineBroadcastBubble()] }));
  }

  removeBubble(uiKey: string): void {
    if (!this.isEditable || this.draft().bubbles.length <= 1) return;
    this.draft.update((d) => ({ ...d, bubbles: d.bubbles.filter((b) => b.uiKey !== uiKey) }));
  }

  moveBubble(index: number, direction: -1 | 1): void {
    if (!this.isEditable) return;
    this.draft.update((d) => {
      const bubbles = [...d.bubbles];
      const target = index + direction;
      if (target < 0 || target >= bubbles.length) return d;
      [bubbles[index], bubbles[target]] = [bubbles[target], bubbles[index]];
      return { ...d, bubbles };
    });
  }

  clearBubbleImage(uiKey: string): void {
    if (!this.isEditable) return;
    this.patchBubble(uiKey, { imagePath: '', imageExt: '', imageUrl: '' });
  }

  onBubbleImageSelected(uiKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.isEditable) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    if (file.size > LINE_BROADCAST_MAX_IMAGE_BYTES) {
      this.toast.error('ไฟล์ใหญ่เกิน 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => void this.uploadBubbleImage(uiKey, String(reader.result || ''), file);
    reader.readAsDataURL(file);
  }

  async onSaveDraft(): Promise<void> {
    const id = await this.saveDraft(true);
    if (id > 0) this.loadDetail(id, false);
  }

  async askSend(): Promise<void> {
    if (!this.isEditable) return;
    const error = validateLineBroadcastDraft(this.draft());
    if (error) {
      this.toast.error(error);
      return;
    }
    const id = await this.saveDraft(false);
    if (id <= 0) return;
    this.loadAudienceAndQuota();
    this.isSendConfirmOpen.set(true);
  }

  closeSendConfirm(): void {
    this.isSendConfirmOpen.set(false);
  }

  askRetry(): void {
    if (!this.canUpdate) return;
    this.isRetryConfirmOpen.set(true);
  }

  closeRetryConfirm(): void {
    this.isRetryConfirmOpen.set(false);
  }

  askDelete(item: LineBroadcastListItem): void {
    if (!this.canDelete || item.status === 'sending') return;
    this.deleteTarget.set(item);
    this.isDeleteConfirmOpen.set(true);
  }

  closeDeleteConfirm(): void {
    this.isDeleteConfirmOpen.set(false);
    this.deleteTarget.set(null);
  }

  openHistory(): void {
    this.showActivityHistory = true;
  }

  closeProgress(): void {
    this.isProgressOpen.set(false);
  }

  /* ---------- Audience: type / nav-bar site / screening ---------- */

  get navSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get navSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  /** monthly_active site scope — editable drafts follow the nav-bar site; sent / read-only keep the saved sites */
  get monthlySiteIds(): number[] {
    if (!this.isEditable) return this.draft().siteIds;
    return this.navSiteId > 0 ? [this.navSiteId] : [];
  }

  get membersTotalPages(): number {
    return Math.max(1, Math.ceil(this.membersTotal() / this.membersPageSize));
  }

  get isWholePageIncluded(): boolean {
    const rows = this.members();
    return rows.length > 0 && rows.every((m) => !this.isExcluded(m.cpUserId));
  }

  get excludedCount(): number {
    return this.draft().excludedUserIds.length;
  }

  get excludedInAudienceCount(): number {
    const summary = this.audienceSummary();
    if (!summary) return 0;
    return this.draft().audienceType === 'monthly_active'
      ? summary.monthlyActive - summary.monthlyActiveSelected
      : summary.allMembers - summary.allMembersSelected;
  }

  setAudience(type: LineBroadcastAudienceType): void {
    if (!this.isEditable || this.draft().audienceType === type) return;
    this.patchDraft({ audienceType: type });
    if (!this.syncNavSite()) this.onAudienceFilterChanged();
  }

  toggleMemberList(): void {
    const next = !this.showMemberList();
    this.showMemberList.set(next);
    if (next) this.loadMembers(1);
  }

  onMembersSearch(): void {
    this.loadMembers(1);
  }

  onMembersPageChange(next: number): void {
    if (next < 1 || next > this.membersTotalPages) return;
    this.loadMembers(next);
  }

  isExcluded(cpUserId: number): boolean {
    return this.draft().excludedUserIds.includes(cpUserId);
  }

  toggleMember(cpUserId: number): void {
    if (!this.isEditable) return;
    const ids = this.draft().excludedUserIds;
    if (ids.includes(cpUserId)) {
      this.setExcluded(ids.filter((id) => id !== cpUserId));
      return;
    }
    if (ids.length >= this.maxExcluded) {
      this.toast.error(`ติ๊กออกได้ไม่เกิน ${this.maxExcluded} คน — ถ้าต้องการตัดมากกว่านี้ให้เลือกกลุ่ม site แทน`);
      return;
    }
    this.setExcluded([...ids, cpUserId]);
  }

  toggleWholePage(): void {
    if (!this.isEditable) return;
    const pageIds = this.members().map((m) => m.cpUserId);
    const current = this.draft().excludedUserIds;
    if (this.isWholePageIncluded) {
      const next = Array.from(new Set([...current, ...pageIds]));
      if (next.length > this.maxExcluded) {
        this.toast.error(`ติ๊กออกได้ไม่เกิน ${this.maxExcluded} คน — ถ้าต้องการตัดมากกว่านี้ให้เลือกกลุ่ม site แทน`);
        return;
      }
      this.setExcluded(next);
    } else {
      this.setExcluded(current.filter((id) => !pageIds.includes(id)));
    }
  }

  clearExcluded(): void {
    if (!this.isEditable || !this.excludedCount) return;
    this.setExcluded([]);
  }

  private setExcluded(ids: number[]): void {
    this.patchDraft({ excludedUserIds: [...ids].sort((a, b) => a - b) });
    this.scheduleSummaryRefresh();
  }

  private onAudienceFilterChanged(): void {
    this.scheduleSummaryRefresh();
    if (this.showMemberList()) this.loadMembers(1);
  }

  private scheduleSummaryRefresh(): void {
    if (this.summaryTimer) clearTimeout(this.summaryTimer);
    this.summaryTimer = setTimeout(() => {
      this.summaryTimer = null;
      this.loadAudienceSummary();
    }, 350);
  }

  private resetMemberList(): void {
    this.showMemberList.set(false);
    this.members.set([]);
    this.membersTotal.set(0);
    this.membersPage.set(1);
    this.membersSearch = '';
    this.audienceSummary.set(null);
  }

  private startPolling(): void {
    this.stopPolling();
    this.loadProgress();
    this.pollTimer = setInterval(() => {
      const id = this.draft().id;
      if (this.viewMode() !== 'edit' || id <= 0 || this.draft().status !== 'sending') {
        this.stopPolling();
        return;
      }
      this.loadProgress();
    }, LineBroadcastsComponent.PollIntervalMs);
  }

  private openProgress(recipientCount: number): void {
    this.progress.set(
      mapLineBroadcastProgress({ id: this.draft().id, status: 'sending', recipientCount, percent: 0 }),
    );
    this.isProgressOpen.set(true);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!menuId) {
      this.toast.error('ไม่พบสิทธิ์เมนูนี้ — ตรวจว่า path /system-settings/line-broadcasts (path_id 129) ถูกผูก role แล้ว');
      return null;
    }
    return { userId: 0, menuId };
  }

  private isOk(res: LineBroadcastActionResponse | null | undefined): boolean {
    return res?.isSuccess === true;
  }

  // ================= API Functions =================

  reload(): void {
    this.isLoading.set(true);
    this.api
      .getLineBroadcastsForTable({
        search: this.searchText,
        status: this.filterStatus() || undefined,
        page: this.page(),
        pageSize: this.pageSize,
      })
      .subscribe({
        next: (res) => {
          console.log('getLineBroadcastsForTable', res);
          this.items.set((res?.items ?? []).map(mapLineBroadcastListItem));
          this.totalCount.set(Number(res?.total ?? 0));
          this.lastFetchedTime.set(new Date());
          this.isLoading.set(false);
        },
        error: () => {
          this.toast.error('โหลดรายการ Broadcast ล้มเหลว');
          this.isLoading.set(false);
        },
      });
  }

  private loadDetail(id: number, showLoading: boolean, silent = false): void {
    if (showLoading) this.isLoading.set(true);
    this.api.getLineBroadcastById(id).subscribe({
      next: (res) => {
        console.log('getLineBroadcastById', res);
        const detail = mapLineBroadcastDetail(res);
        const filterChanged = this.hasAudienceFilterChanged(detail);
        this.draft.set(detail);
        this.viewMode.set('edit');
        this.isLoading.set(false);
        if (!this.syncNavSite() && filterChanged && !silent) {
          this.loadAudienceSummary();
          if (this.showMemberList()) this.loadMembers(1);
        }
        if (detail.status === 'sending') {
          if (!this.pollTimer) this.startPolling();
        } else {
          this.stopPolling();
          if (this.isProgressOpen() && !this.progress()?.isFinished) this.loadProgress();
        }
      },
      error: () => {
        if (!silent) this.toast.error('โหลดรายละเอียด Broadcast ล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  private loadAudienceAndQuota(includeSummary = true): void {
    if (includeSummary) this.loadAudienceSummary();
    if (!this.liffUrl()) this.loadLinkSettings();
    this.api.getLineBroadcastQuota().subscribe({
      next: (res) => {
        console.log('getLineBroadcastQuota', res);
        this.quota.set(mapLineBroadcastQuota(res));
      },
      error: () => this.quota.set(null),
    });
  }

  private async uploadBubbleImage(uiKey: string, base64: string, file: File): Promise<void> {
    const auth = this.resolveAuth();
    if (!auth) return;
    this.uploadingKey.set(uiKey);
    try {
      const res = await firstValueFrom(
        this.api.uploadLineBroadcastImage({
          userId: auth.userId,
          menuId: auth.menuId,
          base64,
          fileName: file.name,
          fileSizeBytes: file.size,
        }),
      );
      console.log('uploadLineBroadcastImage', res);
      if (res?.isSuccess !== true || !res.imagePath || !res.imageExt) {
        this.toast.error(res?.message || 'อัปโหลดรูปไม่สำเร็จ');
        return;
      }
      this.patchBubble(uiKey, {
        imagePath: res.imagePath,
        imageExt: res.imageExt,
        imageUrl: res.imageUrl ?? '',
      });
    } catch {
      this.toast.error('อัปโหลดรูปไม่สำเร็จ');
    } finally {
      this.uploadingKey.set(null);
    }
  }

  /** Returns saved id (0 on failure). */
  private async saveDraft(showToast: boolean): Promise<number> {
    if (!this.isEditable) return 0;
    const error = validateLineBroadcastDraft(this.draft()) ?? this.liffUrlMissingError();
    if (error) {
      this.toast.error(error);
      return 0;
    }
    const auth = this.resolveAuth();
    if (!auth) return 0;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(
        this.api.saveLineBroadcast(toLineBroadcastSavePayload(this.draft(), auth.userId, auth.menuId, this.liffUrl())),
      );
      console.log('saveLineBroadcast', res);
      if (!this.isOk(res)) {
        this.toast.error(res?.message || 'บันทึกไม่สำเร็จ');
        return 0;
      }
      const id = Number(res.idReturn ?? this.draft().id);
      this.patchDraft({ id });
      if (showToast) this.toast.success(res.message || 'บันทึกเรียบร้อย');
      return id;
    } catch {
      this.toast.error('บันทึกแคมเปญล้มเหลว');
      return 0;
    } finally {
      this.isSaving.set(false);
    }
  }

  async onTestSend(): Promise<void> {
    if (!this.isEditable) return;
    const error = validateLineBroadcastDraft({ ...this.draft(), title: this.draft().title || 'test' })
      ?? this.liffUrlMissingError();
    if (error) {
      this.toast.error(error);
      return;
    }
    const auth = this.resolveAuth();
    if (!auth) return;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(
        this.api.testSendLineBroadcast(
          toLineBroadcastTestPayload(this.draft(), auth.userId, auth.menuId, this.testLineUid, this.liffUrl()),
        ),
      );
      console.log('testSendLineBroadcast', res);
      if (!this.isOk(res)) {
        this.toast.error(res?.message || 'ส่งทดสอบไม่สำเร็จ');
        return;
      }
      this.toast.success(res.message || 'ส่งทดสอบแล้ว');
    } catch {
      this.toast.error('ส่งทดสอบไม่สำเร็จ');
    } finally {
      this.isSaving.set(false);
    }
  }

  async confirmSend(): Promise<void> {
    const auth = this.resolveAuth();
    const id = this.draft().id;
    if (!auth || id <= 0) return;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(this.api.sendLineBroadcast({ userId: auth.userId, menuId: auth.menuId, id }));
      console.log('sendLineBroadcast', res);
      if (!this.isOk(res)) {
        this.toast.error(res?.message || 'ส่งไม่สำเร็จ');
        return;
      }
      this.closeSendConfirm();
      this.openProgress(this.selectedAudienceCount ?? 0);
      this.loadDetail(id, false);
    } catch {
      this.toast.error('ส่งไม่สำเร็จ');
    } finally {
      this.isSaving.set(false);
    }
  }

  async confirmRetry(): Promise<void> {
    const auth = this.resolveAuth();
    const id = this.draft().id;
    if (!auth || id <= 0) return;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(this.api.retryLineBroadcast({ userId: auth.userId, menuId: auth.menuId, id }));
      console.log('retryLineBroadcast', res);
      if (!this.isOk(res)) {
        this.toast.error(res?.message || 'ส่งซ้ำไม่สำเร็จ');
        return;
      }
      this.closeRetryConfirm();
      this.openProgress(this.draft().recipientCount);
      this.loadDetail(id, false);
    } catch {
      this.toast.error('ส่งซ้ำไม่สำเร็จ');
    } finally {
      this.isSaving.set(false);
    }
  }

  async confirmDelete(): Promise<void> {
    const item = this.deleteTarget();
    const auth = this.resolveAuth();
    if (!item || !auth) return;

    this.isSaving.set(true);
    try {
      const res = await firstValueFrom(this.api.deleteLineBroadcast({ userId: auth.userId, menuId: auth.menuId, id: item.id }));
      console.log('deleteLineBroadcast', res);
      if (!this.isOk(res)) {
        this.toast.error(res?.message || 'ลบไม่สำเร็จ');
        return;
      }
      this.toast.success(res.message || 'ลบเรียบร้อย');
      this.closeDeleteConfirm();
      this.reload();
    } catch {
      this.toast.error('ลบไม่สำเร็จ');
    } finally {
      this.isSaving.set(false);
    }
  }

  private loadProgress(): void {
    const id = this.draft().id;
    if (id <= 0) return;
    this.api.getLineBroadcastProgress(id).subscribe({
      next: (res) => {
        console.log('getLineBroadcastProgress', res);
        const progress = mapLineBroadcastProgress(res);
        if (progress.id !== this.draft().id) return;
        this.progress.set(progress);
        if (progress.isFinished) {
          this.stopPolling();
          if (this.draft().status === 'sending') this.loadDetail(id, false, true);
        }
      },
      error: () => {
        // polling — keep last known progress; next tick retries
      },
    });
  }

  private hasAudienceFilterChanged(next: LineBroadcastDetail): boolean {
    const cur = this.draft();
    return cur.id !== next.id
      || cur.audienceType !== next.audienceType
      || cur.siteIds.join(',') !== next.siteIds.join(',')
      || cur.excludedUserIds.join(',') !== next.excludedUserIds.join(',');
  }

  private loadAudienceSummary(): void {
    const d = this.draft();
    const seq = ++this.summaryRequestSeq;
    this.api
      .getLineBroadcastAudienceSummary({
        siteIds: this.monthlySiteIds,
        excludedUserIds: d.excludedUserIds,
      })
      .subscribe({
        next: (res) => {
          console.log('getLineBroadcastAudienceSummary', res);
          if (seq !== this.summaryRequestSeq) return;
          this.audienceSummary.set(mapLineBroadcastAudienceSummary(res));
        },
        error: () => {
          if (seq === this.summaryRequestSeq) this.toast.error('โหลดจำนวนผู้รับล้มเหลว');
        },
      });
  }

  private loadMembers(page: number): void {
    const d = this.draft();
    const seq = ++this.membersRequestSeq;
    this.membersPage.set(page);
    this.isMembersLoading.set(true);
    this.api
      .getLineBroadcastAudienceMembers({
        audienceType: d.audienceType,
        siteIds: d.audienceType === 'monthly_active' ? this.monthlySiteIds : [],
        search: this.membersSearch,
        page,
        pageSize: this.membersPageSize,
      })
      .subscribe({
        next: (res) => {
          console.log('getLineBroadcastAudienceMembers', res);
          if (seq !== this.membersRequestSeq) return;
          this.members.set((res?.items ?? []).map(mapLineBroadcastAudienceMember));
          this.membersTotal.set(Number(res?.total ?? 0));
          this.isMembersLoading.set(false);
        },
        error: () => {
          if (seq !== this.membersRequestSeq) return;
          this.toast.error('โหลดรายชื่อสมาชิกล้มเหลว');
          this.isMembersLoading.set(false);
        },
      });
  }

  /* ---------- Message type: image / news / card ---------- */

  get maxImages(): number {
    return lineBroadcastMaxImages(this.draft());
  }

  /** distinct sites of the selected site-news — for the "ส่งหาสมาชิกรายเดือนของลานนี้" shortcut */
  get selectedNewsSiteIds(): number[] {
    const ids = this.draft().news.map((n) => n.siteId).filter((id): id is number => !!id);
    return Array.from(new Set(ids)).sort((a, b) => a - b);
  }

  get selectedNewsSiteNames(): string[] {
    return Array.from(new Set(this.draft().news.filter((n) => n.siteId).map((n) => n.siteName || `#${n.siteId}`)));
  }

  get isNewsSiteOnNav(): boolean {
    return this.selectedNewsSiteIds.includes(this.navSiteId);
  }

  get isAudienceMatchingNewsSites(): boolean {
    return this.draft().audienceType === 'monthly_active' && this.isNewsSiteOnNav;
  }

  messageTypeMeta(type: LineBroadcastMessageType) {
    return LINE_BROADCAST_MESSAGE_TYPE_META[type] ?? LINE_BROADCAST_MESSAGE_TYPE_META.card;
  }

  setMessageType(type: LineBroadcastMessageType): void {
    if (!this.isEditable || this.draft().messageType === type) return;
    this.patchDraft({ messageType: type });
    if (type === 'news' && !this.draft().news.length) this.openNewsPicker();
  }

  onImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length || !this.isEditable) return;
    const slots = this.maxImages - this.draft().images.length;
    if (slots <= 0) {
      this.toast.error(`ส่งรูปได้ไม่เกิน ${this.maxImages} รูป`);
      return;
    }
    if (files.length > slots) this.toast.warn(`เพิ่มได้อีก ${slots} รูป — ใช้ ${slots} รูปแรก`);
    void this.uploadImages(files.slice(0, slots));
  }

  removeImage(uiKey: string): void {
    if (!this.isEditable) return;
    this.draft.update((d) => ({ ...d, images: d.images.filter((x) => x.uiKey !== uiKey) }));
  }

  moveImage(index: number, direction: -1 | 1): void {
    if (!this.isEditable) return;
    this.draft.update((d) => {
      const images = [...d.images];
      const target = index + direction;
      if (target < 0 || target >= images.length) return d;
      [images[index], images[target]] = [images[target], images[index]];
      return { ...d, images };
    });
  }

  openNewsPicker(): void {
    this.showNewsPicker.set(true);
    if (!this.newsOptions().length) this.loadNewsOptions();
  }

  closeNewsPicker(): void {
    this.showNewsPicker.set(false);
  }

  onNewsSearch(): void {
    this.loadNewsOptions();
  }

  isNewsSelected(id: number): boolean {
    return this.draft().news.some((n) => n.id === id);
  }

  toggleNews(option: LineBroadcastNewsOption): void {
    if (!this.isEditable) return;
    const current = this.draft().news;
    if (current.some((n) => n.id === option.id)) {
      this.patchDraft({ news: current.filter((n) => n.id !== option.id) });
      return;
    }
    if (current.length >= this.maxNews) {
      this.toast.error(`เลือกประกาศได้ไม่เกิน ${this.maxNews} รายการ`);
      return;
    }
    const partial: Partial<LineBroadcastDetail> = { news: [...current, option] };
    if (!this.draft().altText.trim()) {
      partial.altText = `ประกาศ: ${option.title}`.slice(0, this.maxAltText);
    }
    this.patchDraft(partial);
  }

  removeNews(id: number): void {
    if (!this.isEditable) return;
    this.patchDraft({ news: this.draft().news.filter((n) => n.id !== id) });
  }

  moveNews(index: number, direction: -1 | 1): void {
    if (!this.isEditable) return;
    this.draft.update((d) => {
      const news = [...d.news];
      const target = index + direction;
      if (target < 0 || target >= news.length) return d;
      [news[index], news[target]] = [news[target], news[index]];
      return { ...d, news };
    });
  }

  applyNewsSiteAudience(): void {
    if (!this.isEditable || !this.isNewsSiteOnNav) return;
    this.setAudience('monthly_active');
    this.toast.success('ตั้งกลุ่มผู้รับเป็นสมาชิกรายเดือนของลานในประกาศแล้ว');
  }

  private resetNewsPicker(): void {
    this.showNewsPicker.set(false);
    this.newsSearch = '';
    this.newsOptions.set([]);
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private async uploadImages(files: File[]): Promise<void> {
    const auth = this.resolveAuth();
    if (!auth) return;
    this.uploadingKey.set(this.imageUploadKey);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          this.toast.error(`${file.name}: กรุณาเลือกไฟล์รูปภาพ`);
          continue;
        }
        if (file.size > LINE_BROADCAST_MAX_IMAGE_BYTES) {
          this.toast.error(`${file.name}: ไฟล์ใหญ่เกิน 10MB`);
          continue;
        }
        try {
          const base64 = await this.readFileAsDataUrl(file);
          const res = await firstValueFrom(
            this.api.uploadLineBroadcastImage({
              userId: auth.userId,
              menuId: auth.menuId,
              base64,
              fileName: file.name,
              fileSizeBytes: file.size,
            }),
          );
          console.log('uploadLineBroadcastImage', res);
          if (res?.isSuccess !== true || !res.imagePath || !res.imageExt) {
            this.toast.error(res?.message || `${file.name}: อัปโหลดรูปไม่สำเร็จ`);
            continue;
          }
          const image = createLineBroadcastImage(res.imagePath, res.imageExt, res.imageUrl ?? '');
          this.draft.update((d) => ({ ...d, images: [...d.images, image] }));
        } catch {
          this.toast.error(`${file.name}: อัปโหลดรูปไม่สำเร็จ`);
        }
      }
    } finally {
      this.uploadingKey.set(null);
    }
  }

  private loadNewsOptions(): void {
    const seq = ++this.newsRequestSeq;
    this.isNewsLoading.set(true);
    this.api.getLineBroadcastNewsOptions(this.newsSearch).subscribe({
      next: (res) => {
        console.log('getLineBroadcastNewsOptions', res);
        if (seq !== this.newsRequestSeq) return;
        this.newsOptions.set((res ?? []).map(mapLineBroadcastNewsOption).filter((x) => x.id > 0));
        this.isNewsLoading.set(false);
      },
      error: () => {
        if (seq !== this.newsRequestSeq) return;
        this.toast.error('โหลดรายการประกาศล้มเหลว');
        this.isNewsLoading.set(false);
      },
    });
  }

  /* ---------- Card button: external URL / page in LIFF ---------- */

  setBubbleLinkType(uiKey: string, linkType: LineBroadcastLinkType): void {
    if (!this.isEditable) return;
    this.patchBubble(uiKey, { linkType });
  }

  setBubbleLiffPath(uiKey: string, path: string): void {
    if (!this.isEditable) return;
    this.patchBubble(uiKey, { liffPath: path });
  }

  liffLinkPreview(bubble: LineBroadcastBubble): string {
    const path = normalizeLiffPath(bubble.liffPath);
    if (!path) return '';
    return buildLiffLink(this.liffUrl() || 'https://liff.line.me/{liffId}', path);
  }

  private liffUrlMissingError(): string | null {
    return needsLiffUrl(this.draft()) && !this.liffUrl()
      ? 'ยังโหลดการตั้งค่าลิงก์ LIFF ไม่สำเร็จ — ลองใหม่อีกครั้ง'
      : null;
  }

  private loadLinkSettings(): void {
    this.api.getLineBroadcastLinkSettings().subscribe({
      next: (res) => {
        console.log('getLineBroadcastLinkSettings', res);
        this.liffUrl.set((res?.liffUrl ?? '').trim());
      },
      error: () => this.liffUrl.set(''),
    });
  }

  /* ---------- Monthly members detail (paid cards, uid = line_id) ---------- */

  readonly detailPageSize = 20;
  showMonthlyDetail = signal(false);
  detailMembers = signal<LineBroadcastAudienceMember[]>([]);
  detailTotal = signal(0);
  detailPage = signal(1);
  detailSearch = '';
  isDetailLoading = signal(false);
  memberCards = signal<LineBroadcastMemberCards | null>(null);
  isMemberCardsLoading = signal(false);
  private detailRequestSeq = 0;
  private memberCardsRequestSeq = 0;

  get detailTotalPages(): number {
    return Math.max(1, Math.ceil(this.detailTotal() / this.detailPageSize));
  }

  openMonthlyDetail(event: Event): void {
    event.stopPropagation();
    this.detailSearch = '';
    this.showMonthlyDetail.set(true);
    this.loadDetailMembers(1);
  }

  closeMonthlyDetail(): void {
    this.showMonthlyDetail.set(false);
    this.closeMemberCards();
  }

  onDetailSearch(): void {
    this.loadDetailMembers(1);
  }

  onDetailPageChange(next: number): void {
    if (next < 1 || next > this.detailTotalPages) return;
    this.loadDetailMembers(next);
  }

  openMemberCards(member: LineBroadcastAudienceMember): void {
    const seq = ++this.memberCardsRequestSeq;
    this.memberCards.set(mapLineBroadcastMemberCards({ cpUserId: member.cpUserId, name: member.name, phone: member.phone }));
    this.isMemberCardsLoading.set(true);
    this.api.getLineBroadcastMemberCards({ cpUserId: member.cpUserId, siteIds: this.monthlySiteIds }).subscribe({
      next: (res) => {
        console.log('getLineBroadcastMemberCards', res);
        if (seq !== this.memberCardsRequestSeq) return;
        this.memberCards.set(mapLineBroadcastMemberCards(res));
        this.isMemberCardsLoading.set(false);
      },
      error: () => {
        if (seq !== this.memberCardsRequestSeq) return;
        this.toast.error('โหลดรายการบัตรล้มเหลว');
        this.isMemberCardsLoading.set(false);
      },
    });
  }

  closeMemberCards(): void {
    this.memberCardsRequestSeq++;
    this.memberCards.set(null);
    this.isMemberCardsLoading.set(false);
  }

  private onNavSiteChanged(): void {
    if (this.viewMode() !== 'edit') return;
    this.closeMonthlyDetail();
    if (!this.syncNavSite()) this.scheduleSummaryRefresh();
  }

  /** Pins an editable monthly_active draft to the nav-bar site. Returns true when the draft changed (refresh already scheduled). */
  private syncNavSite(): boolean {
    const d = this.draft();
    const siteId = this.navSiteId;
    if (this.viewMode() !== 'edit' || !this.isEditable || d.audienceType !== 'monthly_active' || siteId <= 0) return false;
    if (d.siteIds.length === 1 && d.siteIds[0] === siteId) return false;
    this.patchDraft({ siteIds: [siteId], siteNames: [this.navSiteName || `#${siteId}`] });
    this.onAudienceFilterChanged();
    return true;
  }

  private loadDetailMembers(page: number): void {
    const seq = ++this.detailRequestSeq;
    this.detailPage.set(page);
    this.isDetailLoading.set(true);
    this.api
      .getLineBroadcastAudienceMembers({
        audienceType: 'monthly_active',
        siteIds: this.monthlySiteIds,
        search: this.detailSearch,
        page,
        pageSize: this.detailPageSize,
      })
      .subscribe({
        next: (res) => {
          console.log('getLineBroadcastAudienceMembers (detail)', res);
          if (seq !== this.detailRequestSeq) return;
          this.detailMembers.set((res?.items ?? []).map(mapLineBroadcastAudienceMember));
          this.detailTotal.set(Number(res?.total ?? 0));
          this.isDetailLoading.set(false);
        },
        error: () => {
          if (seq !== this.detailRequestSeq) return;
          this.toast.error('โหลดรายชื่อสมาชิกรายเดือนล้มเหลว');
          this.isDetailLoading.set(false);
        },
      });
  }
}
